import {useState} from "react";
import {Checkbox} from "biggerui";
import {API} from "../../api";
import {useAuth} from "../../hooks/useAuth";
import {VIEW_AS_ROLES} from "../../data/permissions";

import "./index.css";

/** The role that the picker shows before the superadmin selects one: the role below superadmin. */
const FIRST_VIEW_AS_ROLE = "admin";

/**
 * Start, change or stop to view the app as another role. Then a page loads
 * again: useAuth caches the role for the session, and each part of the app
 * reads the role from it.
 *
 * @param nextUrl - The page to open with a new view-as role. Without it, the
 *   current page loads again. Stop always loads the current page again.
 */
function useSwitchViewAsRole(nextUrl?: string) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const switchTo = async (role: string | null) => {
    setIsPending(true);
    setError(null);
    try {
      await API.setViewAsRole(role);
      if (role && nextUrl) window.location.assign(nextUrl);
      else window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setIsPending(false);
    }
  };
  return {switchTo, isPending, error};
}

function RolePicker({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (role: string) => void;
}) {
  return (
    <select
      className="view-as-role-select"
      aria-label="Role to view the app as"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {VIEW_AS_ROLES.map((r) => (
        <option key={r} value={r}>
          {r}
        </option>
      ))}
    </select>
  );
}

/**
 * The admin console control: "View as role" and a role picker. Only for a real
 * superadmin. A new role opens /admin, which goes to the first section that
 * the role can open.
 */
export function ViewAsRoleControl() {
  const {role, realRole} = useAuth();
  const isViewing = role !== realRole;
  const [picked, setPicked] = useState(isViewing ? role : FIRST_VIEW_AS_ROLE);
  const {switchTo, isPending, error} = useSwitchViewAsRole("/admin");
  if (realRole !== "superadmin") return null;

  return (
    <div className="view-as-role-control" data-testid="view-as-role-control">
      <Checkbox
        checked={isViewing}
        disabled={isPending}
        label="View as role"
        onChange={(checked) => switchTo(checked ? picked : null)}
      />
      <RolePicker
        value={isViewing ? role : picked}
        disabled={isPending}
        onChange={(r) => (isViewing ? switchTo(r) : setPicked(r))}
      />
      {error && <span className="view-as-role-error">{error}</span>}
    </div>
  );
}

/**
 * A bar on the page while a superadmin views the app as another role. The
 * superadmin always sees the role, and can change or stop it also where the
 * role has no admin console.
 */
export function ViewAsRoleBar() {
  const {role, realRole} = useAuth();
  const {switchTo, isPending, error} = useSwitchViewAsRole();
  if (role === realRole) return null;

  return (
    <div className="view-as-role-bar" role="status" data-testid="view-as-role-bar">
      <span>Viewing as</span>
      <RolePicker value={role} disabled={isPending} onChange={switchTo} />
      <button
        type="button"
        className="view-as-role-exit"
        disabled={isPending}
        onClick={() => switchTo(null)}
      >
        Exit
      </button>
      {error && <span className="view-as-role-error">{error}</span>}
    </div>
  );
}
